import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { X, ArrowLeft, Phone, MessageSquare, Mail, FileText, Send, Sparkles, Upload, Loader2, CheckCircle, DollarSign, Pencil, Save, CheckCircle2, Circle, Megaphone, Trash2 } from 'lucide-react';
import { InvoiceBuilder } from './InvoiceBuilder';
import { tplMeta, TYPE_ICON_CLS } from './templateMeta';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p || 0);
const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };
const API = process.env.REACT_APP_BACKEND_URL;

const CircleBtn = ({ href, icon: Icon, label, testId }) => (
  <a href={href} className="w-11 h-11 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-zinc-300 active:scale-95 transition-transform" data-testid={testId} aria-label={label}>
    <Icon className="w-[18px] h-[18px]" />
  </a>
);

const Field = ({ label, value, onChange, type = 'text', testId }) => (
  <div>
    <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
           className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30"
           data-testid={testId} />
  </div>
);

const Bullets = ({ text }) => {
  const lines = (text || '').split('\n').map(l => l.trim()).filter(Boolean);
  return (
    <ul className="space-y-2.5" data-testid="lead-summary-text">
      {lines.map((l, i) => (
        <li key={i} className="flex gap-3 text-zinc-300 text-sm leading-relaxed" style={FONT_B}>
          <span className="w-1 h-1 rounded-full bg-blue-400 mt-[9px] flex-shrink-0" />
          <span>{l.replace(/^[-•*]\s*/, '')}</span>
        </li>
      ))}
    </ul>
  );
};

const when = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

const ATTR_LABELS = { utm_source: 'Source', utm_medium: 'Medium', utm_campaign: 'Campaign', utm_term: 'Search term', utm_content: 'Ad content' };

function Attribution({ attr }) {
  if (!attr) return null;
  let refHost = null;
  try { refHost = attr.referrer ? new URL(attr.referrer).hostname.replace('www.', '') : null; } catch { refHost = attr.referrer; }
  const chips = [];
  if (attr.gclid) chips.push(['Google Ads click', 'bg-emerald-500/10 text-emerald-400']);
  if (attr.fbclid) chips.push(['Meta Ads click', 'bg-blue-500/10 text-blue-400']);
  Object.entries(ATTR_LABELS).forEach(([k, label]) => { if (attr[k]) chips.push([`${label}: ${attr[k]}`, 'bg-white/5 text-zinc-300']); });
  if (refHost) chips.push([`From ${refHost}`, 'bg-white/5 text-zinc-400']);
  if (!chips.length && !attr.landing_page) return null;
  return (
    <div data-testid="lead-attribution">
      <p className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 flex items-center gap-1.5" style={FONT_B}>
        <Megaphone className="w-3 h-3" /> How They Found You
      </p>
      <div className="flex flex-wrap gap-1.5">
        {chips.length === 0 && <span className="text-[11px] px-2.5 py-1.5 rounded-lg bg-white/5 text-zinc-400 font-bold" style={FONT_B}>Direct visit</span>}
        {chips.map(([label, cls], i) => (
          <span key={i} className={`text-[11px] px-2.5 py-1.5 rounded-lg font-bold ${cls}`} style={FONT_B}>{label}</span>
        ))}
      </div>
      {attr.landing_page && (
        <p className="text-zinc-600 text-[11px] mt-2.5 truncate" style={FONT_B}>Landed on <span className="text-zinc-400">{attr.landing_page}</span>{attr.first_seen_at ? ` · ${new Date(attr.first_seen_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</p>
      )}
    </div>
  );
}

export const LeadDetail = ({ proposalId, onClose }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('details');
  const [expandedItem, setExpandedItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get(`/api/admin/leads/${proposalId}`));
    } catch (e) {
      toast.error('Failed to load lead');
    }
  }, [proposalId]);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.message || 'Failed'); }
    setBusy('');
  };

  const startEdit = (p) => {
    setForm({
      client_name: p.client_name || '', client_email: p.client_email || '', client_phone: p.client_phone || '',
      event_date: p.event_date || '', event_time: p.event_time || '', event_location: p.event_location || '',
      event_type: p.event_type || '', guest_count: p.guest_count || '', notes: p.notes || '',
    });
    setEditing(true);
    setTab('details');
  };

  const saveEdit = () => act('edit', async () => {
    await api.put(`/api/admin/proposals/${proposalId}/edit`, form);
    setEditing(false);
  }, 'Saved');

  const deleteLead = async () => {
    if (!window.confirm(`Permanently delete ${data?.proposal?.client_name || 'this lead'} and all their history? This cannot be undone.`)) return;
    setBusy('delete');
    try {
      await api.delete(`/api/admin/leads/${proposalId}`);
      toast.success('Lead deleted');
      onClose();
    } catch (e) { toast.error(e.message || 'Delete failed'); setBusy(''); }
  };

  const addNote = () => {
    if (!noteText.trim()) return;
    act('note', async () => {
      await api.post(`/api/admin/proposals/${proposalId}/notes`, { text: noteText.trim() });
      setNoteText('');
    }, 'Note added');
  };

  const uploadCall = async (file) => {
    setBusy('call');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/admin/leads/${proposalId}/calls/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Upload failed');
      toast.success('Call transcribed!');
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  if (!data) return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" data-testid="lead-detail-loading">
      <Loader2 className="w-8 h-8 text-white animate-spin" />
    </div>
  );

  const p = data.proposal;
  const m = data.money;
  const setF = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const activity = [
    ...(data.messages || []).map(msg => ({ ...msg, _type: 'text' })),
    ...(data.calls || []).map(c => ({ ...c, _type: 'call', id: c.call_id })),
  ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto" data-testid="lead-detail">
      <div className="max-w-2xl mx-auto min-h-screen bg-[#050505] border-x border-white/5">

        {/* Sticky header */}
        <div className="sticky top-0 z-20 backdrop-blur-2xl bg-black/70 border-b border-white/5 px-6 pt-5 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <button onClick={onClose} className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full text-zinc-400 active:scale-95 flex-shrink-0 transition-transform" data-testid="lead-back-btn" aria-label="Back">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h2 className="text-white text-[24px] font-black tracking-tighter truncate leading-tight" style={FONT_H} data-testid="lead-client-name">{p.client_name}</h2>
                <p className="text-zinc-500 text-xs mt-1" style={FONT_B}>
                  {p.event_type || 'Event'}{p.event_date ? ` · ${p.event_date}` : ''} · <span className="uppercase tracking-wider text-zinc-300 font-bold">{(p.status || '').replace(/_/g, ' ')}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!editing && (
                <button onClick={() => startEdit(p)} className="w-11 h-11 flex items-center justify-center rounded-full text-zinc-500 active:scale-95 transition-transform" data-testid="lead-edit-btn" aria-label="Edit lead">
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              <button onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-full bg-white/5 text-zinc-300 active:scale-95 transition-transform" data-testid="lead-detail-close" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2.5 mt-4">
            {p.client_phone && <CircleBtn href={`tel:${p.client_phone}`} icon={Phone} label="Call" testId="lead-call" />}
            {p.client_phone && <CircleBtn href={`sms:${p.client_phone}`} icon={MessageSquare} label="Text" testId="lead-text" />}
            {p.client_email && <CircleBtn href={`mailto:${p.client_email}`} icon={Mail} label="Email" testId="lead-email" />}
            <span className="text-zinc-600 text-xs ml-1 truncate" style={FONT_B}>{p.client_phone || p.client_email || ''}</span>
          </div>
        </div>

        <div className="px-6 py-6 space-y-9 pb-16">

          {/* Money */}
          <div data-testid="lead-money">
            <div className="flex items-center justify-between mb-3">
              <p className="text-zinc-500 text-[11px] uppercase tracking-[0.2em] font-semibold" style={FONT_B}>Money</p>
              <button onClick={() => setInvoiceOpen(true)}
                      className="flex items-center gap-1.5 text-zinc-500 text-xs font-bold transition-colors hover:text-white" style={FONT_B} data-testid="lead-edit-pricing">
                <Pencil className="w-3 h-3" /> Edit Invoice
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              {[['Total', m.total, 'text-white'], ['Paid', m.paid, m.paid > 0 ? 'text-emerald-400' : 'text-zinc-600'], ['Balance', m.balance, m.balance > 0 ? 'text-amber-400' : 'text-zinc-600']].map(([label, val, cls]) => (
                <div key={label} className="bg-[#111111] border border-white/5 rounded-2xl py-4 text-center">
                  <p className={`text-[19px] font-black tracking-tight tabular-nums ${cls}`} style={FONT_H}>{fmt(val)}</p>
                  <p className="text-zinc-600 text-[9px] uppercase tracking-[0.2em] font-semibold mt-1" style={FONT_B}>{label}</p>
                </div>
              ))}
            </div>
            {m.vendor_cost > 0 && (
              <p className="text-zinc-500 text-xs mb-4" style={FONT_B} data-testid="lead-margin">
                Vendor cost {fmt(m.vendor_cost)} · <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 font-bold tabular-nums">margin {fmt(m.margin)}</span>
              </p>
            )}
            <div className="flex flex-wrap gap-2.5">
                {(p.payment_url || p.ghl_invoice_id) && (
                  <a href={p.payment_url || `https://link.msgsndr.com/invoice/${p.ghl_invoice_id}`} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-bold active:scale-95 transition-transform" style={FONT_B} data-testid="lead-view-invoice">
                    <FileText className="w-4 h-4" /> View Invoice
                  </a>
                )}
                {p.ghl_invoice_id && (
                  <button onClick={() => act('send', () => api.post(`/api/admin/leads/${proposalId}/send-invoice`, {}), 'Invoice sent')}
                          disabled={busy === 'send'} className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-white text-sm font-bold active:scale-95 disabled:opacity-40 transition-transform" style={FONT_B} data-testid="lead-send-invoice">
                    <Send className="w-4 h-4" /> {busy === 'send' ? 'Sending…' : 'Send Invoice'}
                  </button>
                )}
                {m.balance > 0 && (
                  <button onClick={() => act('bal', () => api.post(`/api/admin/leads/${proposalId}/request-balance`, {}), 'Balance link texted to client')}
                          disabled={busy === 'bal'} className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-white text-sm font-bold active:scale-95 disabled:opacity-40 transition-transform" style={FONT_B} data-testid="lead-request-balance">
                    <DollarSign className="w-4 h-4" /> {busy === 'bal' ? 'Sending…' : 'Request Balance'}
                  </button>
                )}
            </div>
            {p.payment_received_at && (
              <p className="text-emerald-500/80 text-xs flex items-center gap-1.5 mt-3" style={FONT_B}>
                <CheckCircle className="w-3.5 h-3.5" /> Deposit received {p.payment_received_at.slice(0, 10)}{p.balance_paid_at ? ` · Balance paid ${p.balance_paid_at.slice(0, 10)}` : ''}
              </p>
            )}
          </div>

          {/* AI Briefing */}
          <div className="bg-[#111111] border border-blue-500/15 rounded-2xl p-5 shadow-[0_0_30px_rgba(59,130,246,0.05)]" data-testid="lead-briefing">
            <p className="text-blue-400 text-[11px] uppercase tracking-[0.2em] font-semibold mb-4 flex items-center gap-2" style={FONT_B}>
              <Sparkles className="w-3.5 h-3.5" /> AI Briefing
            </p>
            {data.ai_summary && <div className="mb-4"><Bullets text={data.ai_summary.text} /></div>}
            <button onClick={() => act('sum', () => api.post(`/api/admin/leads/${proposalId}/summary`, {}), 'Briefing updated')}
                    disabled={busy === 'sum'} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-xs font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid="lead-generate-summary">
              <Sparkles className="w-3.5 h-3.5" /> {busy === 'sum' ? 'Thinking…' : data.ai_summary ? 'Refresh Briefing' : 'Generate Briefing'}
            </button>
          </div>

          {/* Tabs */}
          <div>
            <div className="flex gap-2 mb-6" data-testid="lead-tabs">
              {[['details', 'Details'], ['activity', `Activity${activity.length ? ` ${activity.length}` : ''}`], ['notes', `Notes${(p.notes_log || []).length ? ` ${p.notes_log.length}` : ''}`]].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)}
                        className={`px-5 py-2.5 rounded-full text-[13px] font-bold transition-colors ${tab === k ? 'bg-white text-black' : 'bg-white/5 text-zinc-500 border border-white/10'}`}
                        style={FONT_B} data-testid={`lead-tab-${k}`}>
                  {label}
                </button>
              ))}
            </div>

            {tab === 'details' && !editing && (
              <div className="space-y-7" data-testid="lead-details">
                <div className="grid grid-cols-2 gap-x-6 gap-y-5 text-sm">
                  {[['Date', p.event_date || 'TBD'], ['Time', p.event_time || 'TBD'], ['Location', p.event_location || 'TBD'], ['Guests', p.guest_count || '—'],
                    ['Contract', p.esign ? `Signed — ${p.esign.signature_name}` : p.ghl_contract_id ? 'Sent via GHL' : 'Not sent',
                      p.esign ? 'text-emerald-400' : p.ghl_contract_id ? 'text-cyan-400' : 'text-zinc-500'],
                    ['Source', p.source === 'phone_in' ? 'Phone-in' : p.source === 'invoice' ? 'Invoice' : p.source === 'ai_brain' ? 'Ask AI' : 'Website',
                      p.source === 'phone_in' ? 'text-violet-400' : 'text-zinc-200']].map(([label, val, cls]) => (
                    <div key={label}>
                      <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1" style={FONT_B}>{label}</span>
                      <span className={cls || 'text-zinc-200'} style={FONT_B}>{val}</span>
                    </div>
                  ))}
                </div>
                {(p.items || []).length > 0 && (
                  <div>
                    <p className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold mb-3" style={FONT_B}>Line Items</p>
                    <div className="space-y-2.5">
                      {p.items.map((i, idx) => (
                        <div key={idx} className="flex justify-between text-sm" style={FONT_B}>
                          <span className="text-zinc-300">{i.name}{i.quantity > 1 ? ` x${i.quantity}` : ''}{i.hours > 1 ? ` · ${i.hours}hrs` : ''}</span>
                          <span className="text-zinc-500 tabular-nums">{i.quotePending ? 'Quote pending' : fmt((i.price || 0) + (i.additionalHourPrice || 0) * ((i.hours || 1) - 1))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <Attribution attr={p.attribution} />
                {p.notes && (
                  <div>
                    <p className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold mb-2" style={FONT_B}>Client Notes</p>
                    <p className="text-zinc-400 text-sm leading-relaxed" style={FONT_B}>{p.notes}</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'details' && editing && (
              <div className="space-y-4" data-testid="lead-edit-form">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Name" value={form.client_name} onChange={setF('client_name')} testId="lead-edit-name" />
                  <Field label="Phone" value={form.client_phone} onChange={setF('client_phone')} type="tel" testId="lead-edit-phone" />
                  <div className="col-span-2"><Field label="Email" value={form.client_email} onChange={setF('client_email')} type="email" testId="lead-edit-email" /></div>
                  <Field label="Event Date" value={form.event_date} onChange={setF('event_date')} type="date" testId="lead-edit-date" />
                  <Field label="Time" value={form.event_time} onChange={setF('event_time')} testId="lead-edit-time" />
                  <Field label="Event Type" value={form.event_type} onChange={setF('event_type')} testId="lead-edit-type" />
                  <Field label="Guests" value={form.guest_count} onChange={(v) => setF('guest_count')(parseInt(v) || '')} type="number" testId="lead-edit-guests" />
                  <div className="col-span-2"><Field label="Location" value={form.event_location} onChange={setF('event_location')} testId="lead-edit-location" /></div>
                </div>
                <div>
                  <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>Notes</span>
                  <textarea value={form.notes} onChange={(e) => setF('notes')(e.target.value)} rows={2}
                            className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30 resize-none" data-testid="lead-edit-notes" />
                </div>
                <div className="flex gap-2.5">
                  <button onClick={saveEdit} disabled={busy === 'edit'} className="flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid="lead-save-edit">
                    {busy === 'edit' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                  </button>
                  <button onClick={() => setEditing(false)} className="px-6 py-3 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-sm font-bold active:scale-95 transition-transform" style={FONT_B} data-testid="lead-cancel-edit">Cancel</button>
                </div>
                <button onClick={deleteLead} disabled={busy === 'delete'}
                        className="flex items-center gap-1.5 text-red-400/70 text-xs font-bold mt-4 hover:text-red-400 disabled:opacity-40 transition-colors" style={FONT_B} data-testid="lead-delete-btn">
                  {busy === 'delete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete this lead permanently
                </button>
              </div>
            )}

            {tab === 'activity' && (
              <div data-testid="lead-calls">
                <input ref={fileRef} type="file" accept=".mp3,.m4a,.wav,.webm,.mp4" className="hidden" onChange={(e) => e.target.files[0] && uploadCall(e.target.files[0])} data-testid="call-file-input" />
                <button onClick={() => fileRef.current.click()} disabled={busy === 'call'}
                        className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-sm font-bold active:scale-95 disabled:opacity-40 mb-5 transition-transform" style={FONT_B} data-testid="lead-upload-call">
                  <Upload className="w-4 h-4" /> {busy === 'call' ? 'Transcribing…' : 'Upload Call Recording'}
                </button>
                {activity.length === 0 && <p className="text-zinc-600 text-xs leading-relaxed" style={FONT_B}>No activity yet. Texts and call transcripts will show here, newest first.</p>}
                <div className="space-y-3">
                  {activity.map(item => (
                    <div key={`${item._type}-${item.id}`} className="bg-[#111111] border border-white/5 rounded-2xl p-4 cursor-pointer"
                         onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)} data-testid={`lead-activity-${item.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${TYPE_ICON_CLS[item._type]}`}>
                          {item._type === 'call' ? <Phone className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`text-[13px] font-bold ${item._type === 'call' ? 'text-blue-400' : tplMeta(item.template).text}`} style={FONT_B}>
                            {item._type === 'call' ? (item.filename || 'Call transcript') : tplMeta(item.template).label}
                          </p>
                          <p className="text-zinc-600 text-[11px] flex items-center gap-1.5" style={FONT_B}>
                            {when(item.created_at)}
                            {item._type === 'text' && (item.sent
                              ? <span className="flex items-center gap-1 text-emerald-500"><CheckCircle2 className="w-3 h-3" />sent</span>
                              : <span className="flex items-center gap-1 text-amber-400"><Circle className="w-3 h-3" />queued</span>)}
                          </p>
                        </div>
                      </div>
                      <p className={`text-zinc-400 text-[13px] leading-relaxed mt-2.5 whitespace-pre-wrap ${expandedItem === item.id ? '' : 'line-clamp-2'}`} style={FONT_B}>
                        {item._type === 'call' ? item.transcript : item.message}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'notes' && (
              <div className="space-y-5" data-testid="lead-notes">
                <div className="flex gap-2">
                  <input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNote()}
                         placeholder="Add a note…" className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-white/30" style={FONT_B} data-testid="lead-note-input" />
                  <button onClick={addNote} disabled={busy === 'note'} className="px-6 py-3 rounded-full bg-white text-black text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid="lead-note-save">
                    {busy === 'note' ? '…' : 'Add'}
                  </button>
                </div>
                {(p.notes_log || []).length === 0 && <p className="text-zinc-600 text-xs" style={FONT_B}>No notes yet.</p>}
                <div className="space-y-4">
                  {(p.notes_log || []).slice().reverse().map((n, i) => (
                    <div key={i} className="text-sm">
                      <p className="text-zinc-300" style={FONT_B}>{n.text}</p>
                      <p className="text-zinc-700 text-[11px] mt-0.5" style={FONT_B}>{n.created_at?.slice(0, 16).replace('T', ' ')} · {n.author}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {invoiceOpen && (
        <InvoiceBuilder
          proposalId={proposalId}
          onClose={() => { setInvoiceOpen(false); load(); }}
          onSaved={() => { setInvoiceOpen(false); load(); }}
        />
      )}
    </div>
  );
};
